import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, Wallet, CalendarClock, TrendingUp, Users, PackageCheck, Receipt, ClipboardList, ScanBarcode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { money, fmtDate } from '../lib/share';
import PriceBControl from './PriceBControl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Scanner } from './UnitsDialog';
import { toast } from 'sonner';

const Kpi = ({ icon: Icon, label, value, sub, tone = 'emerald', testid, onClick }) => {
  const tones = { emerald: 'from-[#022C22] to-[#064E3B] text-white', gold: 'from-[#B8860B] to-[#D4AF37] text-[#022C22]', red: 'from-[#7F1D1D] to-[#B91C1C] text-white', light: 'from-white to-[#F8FAFC] text-[#022C22] border border-[#E2E8F0]' };
  return (
    <button onClick={onClick} className={`text-left rounded-2xl p-4 bg-gradient-to-br ${tones[tone]} shadow-sm hover:-translate-y-0.5 transition-transform`} data-testid={testid}>
      <div className="flex items-center justify-between opacity-80 text-xs font-semibold uppercase tracking-wide"><span>{label}</span><Icon size={16} /></div>
      <p className="font-heading text-2xl sm:text-3xl font-black mt-2">{value}</p>
      {sub && <p className="text-xs opacity-75 mt-1">{sub}</p>}
    </button>
  );
};

// Dashboard Celular Beach: indicadores de crédito y cobranza, total y por tienda
export const CreditDashboard = ({ isManager }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const owner = ['master', 'superadmin'].includes(user?.role_id);
  const [cid, setCid] = useState('');
  const [companies, setCompanies] = useState([]);
  const [d, setD] = useState(null);
  useEffect(() => { if (owner) api.getCompanies().then((r) => setCompanies(r.data)).catch(() => {}); }, [owner]);
  useEffect(() => { api.getCreditDashboard({ company_id: cid || undefined }).then((r) => setD(r.data)).catch(() => {}); }, [cid]);
  const [scanOpen, setScanOpen] = useState(false);
  const quickScan = async (code) => {
    setScanOpen(false);
    try {
      const r = await api.scanProduct(code, cid || undefined);
      const p = r.data.product;
      const where = p.company_name ? ` · ${p.company_name}` : '';
      toast.success(`${p.name}${p.marca ? ' · ' + p.marca : ''} · Stock: ${p.stock} · $${(p.price || 0).toFixed(2)}${r.data.imei ? ' · IMEI ' + r.data.imei : ''}${where}`);
      nav('/inventory');
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Artículo no encontrado'); }
  };
  if (!d) return <div className="p-6 text-[#64748B]">Cargando indicadores…</div>;
  const g = d.global;
  const max = Math.max(1, ...d.series.map((s) => s.collected));
  return (
    <div className="space-y-5" data-testid="credit-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-bold">Resumen financiero · {fmtDate(d.date)}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#022C22]">Hola, {user?.full_name?.split(' ')[0]}</h1>
        </div>
        {owner && (
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="px-3 py-2 border border-[#E2E8F0] rounded-full text-sm bg-white" data-testid="dashboard-branch-filter">
            <option value="">{user?.role_id === 'master' ? 'Todas las tiendas (global)' : 'Todas mis sucursales'}</option>
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        )}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setScanOpen(true)} data-testid="dashboard-scan-btn"><ScanBarcode size={15} className="mr-1" /> Escanear producto</Button>
      </div>
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-sm" data-testid="dashboard-scan-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanBarcode size={18} /> Buscar producto por código / IMEI</DialogTitle></DialogHeader>
          {scanOpen && <Scanner scanId="dashboard-scanner" onCode={quickScan} onClose={() => setScanOpen(false)} />}
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Wallet} label="Cobrado hoy" value={money(g.collected_today)} tone="emerald" testid="kpi-collected-today" />
        <Kpi icon={CalendarClock} label="Por cobrar hoy" value={money(g.due_today)} sub={`${g.due_today_count} cuota(s)`} tone="gold" testid="kpi-due-today" onClick={() => nav('/collections')} />
        <Kpi icon={AlertTriangle} label="Vencido sin cobrar" value={money(g.overdue_amount)} sub={`${g.overdue_count} cuotas · ${g.overdue_clients} clientes`} tone="red" testid="kpi-overdue" onClick={() => nav('/collections')} />
        <Kpi icon={TrendingUp} label="Cartera por cobrar" value={money(g.receivable)} sub={`Próx. 7 días: ${money(g.next7)}`} tone="light" testid="kpi-receivable" />
        <Kpi icon={Receipt} label="Recibido en el mes" value={money(g.collected_month)} tone="light" testid="kpi-collected-month" />
        <Kpi icon={TrendingUp} label="Ventas del mes" value={money(g.sales_month)} sub={`${g.sales_month_count} venta(s) · crédito ${money(g.credit_sales_month)}`} tone="light" testid="kpi-sales-month" />
        <Kpi icon={ClipboardList} label="Pendientes" value={`${d.pending.reservations} res · ${d.pending.receipts} comp`} sub={`${d.pending.applications} solicitudes de crédito`} tone="light" testid="kpi-pending" onClick={() => nav('/credit-queue')} />
        <Kpi icon={PackageCheck} label="Equipos disponibles (IMEI)" value={d.pending.units_available} tone="light" testid="kpi-units" onClick={() => nav('/inventory')} />
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        <Card><CardContent className="p-4">
          <h3 className="font-heading font-bold text-[#022C22] mb-3">Cobranza últimos 7 días</h3>
          <div className="flex items-end gap-2 h-40" data-testid="collections-chart">
            {d.series.map((s) => (
              <div key={s.day} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-[#64748B]">{s.collected ? money(s.collected) : ''}</span>
                <div className="w-full rounded-t-md bg-gradient-to-t from-[#022C22] to-[#10B981]" style={{ height: `${(s.collected / max) * 100}%`, minHeight: 3 }} />
                <span className="text-[10px] text-[#64748B]">{s.day}</span>
              </div>))}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h3 className="font-heading font-bold text-[#022C22] mb-2 flex items-center gap-2"><Users size={16} /> Clientes en mora</h3>
          {d.top_debtors.length === 0 ? <p className="text-sm text-[#94A3B8]">Sin cuentas vencidas.</p> : d.top_debtors.map((x) => (
            <div key={x.client_id} className="flex justify-between text-sm border-b border-[#F1F5F9] py-1.5"><span>{x.name}<span className="block text-[10px] text-[#94A3B8]">{x.company} · desde {fmtDate(x.oldest)}</span></span><b className="text-red-600">{money(x.amount)}</b></div>))}
        </CardContent></Card>
      </div>
      {d.branches.length > 0 && (
        <Card><CardContent className="p-4 overflow-x-auto" data-testid="dashboard-branches">
          <h3 className="font-heading font-bold text-[#022C22] mb-2">Por tienda</h3>
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-[#64748B] border-b"><th className="py-2">Tienda</th><th>Cobrado hoy</th><th>Por cobrar hoy</th><th>Vencido</th><th>Cartera</th><th>Ventas mes</th></tr></thead>
            <tbody>{d.branches.map((b) => <tr key={b.company_id} className="border-b border-[#F1F5F9] cursor-pointer hover:bg-[#F8FAFC]" onClick={() => setCid(b.company_id)}><td className="py-2 font-medium">{b.name}</td><td>{money(b.collected_today)}</td><td>{money(b.due_today)}</td><td className="text-red-600">{money(b.overdue_amount)}</td><td>{money(b.receivable)}</td><td>{money(b.sales_month)}</td></tr>)}</tbody></table>
        </CardContent></Card>
      )}
      {isManager && <PriceBControl />}
    </div>
  );
};

export default CreditDashboard;
